'use client'

import { ChevronRight, ExpandMore, MoreHoriz } from '@mui/icons-material'
import { Box, ButtonBase, Menu, MenuItem, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslations } from 'next-intl'
import { useLayoutEffect, useRef, useState } from 'react'

import OrgChildrenMenu from './OrgChildrenMenu'
import type { OrgNode } from './types'

type OrgBreadcrumbProps = {
  path: OrgNode[]
  childOrgs: OrgNode[]
  /** True while the org-tree customers query has not settled. */
  childrenLoading?: boolean
  onSelect: (orgId: string) => void
}

const CURRENT_NAME_MAX_PX = 280

export default function OrgBreadcrumb({
  path,
  childOrgs,
  childrenLoading = false,
  onSelect
}: OrgBreadcrumbProps) {
  const t = useTranslations('orgBar')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const listRef = useRef<HTMLOListElement>(null)

  const [overflowOpen, setOverflowOpen] = useState(false)
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null)
  const [hiddenIds, setHiddenIds] = useState<string[]>([])

  const [menuOpen, setMenuOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  const current = path.at(-1)
  const ancestors = path.slice(0, -1)
  const home = path[0]

  // Re-measure on container resize (sidebar, window, etc.) — path/isMobile alone miss shrinks.
  // Re-bind when `path` changes so we observe after the early-return null → list mount.
  const [containerWidth, setContainerWidth] = useState(0)
  useLayoutEffect(() => {
    const el = listRef.current
    if (!el || typeof ResizeObserver === 'undefined') return

    const updateWidth = () => {
      const next = Math.round(el.clientWidth)
      setContainerWidth((prev) => (prev === next ? prev : next))
    }
    updateWidth()
    const ro = new ResizeObserver(updateWidth)
    ro.observe(el)
    return () => ro.disconnect()
  }, [path])

  // Expand → measure → collapse. Reset to full trail whenever width/path changes so a
  // later shrink can put middle crumbs into the … menu (and a widen can restore them).
  // Two layout passes stay before paint; once collapsed, scrollWidth shrinks so we do
  // not oscillate hide↔show at the same width.
  useLayoutEffect(() => {
    setHiddenIds((prev) => (prev.length === 0 ? prev : []))
  }, [path, isMobile, containerWidth])

  useLayoutEffect(() => {
    if (isMobile || !listRef.current || path.length <= 2) return
    if (hiddenIds.length > 0) return

    const el = listRef.current
    if (el.scrollWidth <= el.clientWidth) return

    const middle = path.slice(1, -1).map((node) => node.id)
    setHiddenIds((prev) => (sameIdList(prev, middle) ? prev : middle))
  }, [path, isMobile, containerWidth, hiddenIds])

  if (!current || !home) return null

  const visibleAncestors = isMobile
    ? []
    : ancestors.filter((a) => a.id === home.id || !hiddenIds.includes(a.id))
  const hiddenAncestors = ancestors.filter((a) => hiddenIds.includes(a.id))

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
      <Box
        component="ol"
        ref={listRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          m: 0,
          p: 0,
          listStyle: 'none',
          minWidth: 0,
          overflow: 'hidden'
        }}
      >
        {visibleAncestors.map((ancestor, index) => (
          <Box
            component="li"
            key={ancestor.id}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
          >
            {index > 0 && (
              <ChevronRight sx={{ fontSize: 16, color: 'text.disabled' }} aria-hidden />
            )}
            <CrumbButton onClick={() => onSelect(ancestor.id)}>{ancestor.name}</CrumbButton>
            {index === 0 && hiddenAncestors.length > 0 && (
              <>
                <ChevronRight sx={{ fontSize: 16, color: 'text.disabled' }} aria-hidden />
                <ButtonBase
                  aria-label={t('overflowAncestors')}
                  onClick={(e) => {
                    setOverflowAnchor(e.currentTarget)
                    setOverflowOpen(true)
                  }}
                  sx={crumbSx}
                >
                  <MoreHoriz sx={{ fontSize: 16, color: 'text.secondary' }} />
                </ButtonBase>
              </>
            )}
          </Box>
        ))}

        {!isMobile && ancestors.length > 0 && (
          <Box component="li" sx={{ display: 'flex', alignItems: 'center' }} aria-hidden>
            <ChevronRight sx={{ fontSize: 16, color: 'text.disabled' }} />
          </Box>
        )}

        <Box component="li" sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Tooltip title={current.name} disableHoverListener={current.name.length < 24}>
            <ButtonBase
              aria-current="location"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={(e) => {
                setMenuAnchor(e.currentTarget)
                setMenuOpen(true)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setMenuAnchor(e.currentTarget)
                  setMenuOpen(true)
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                color: 'text.primary',
                maxWidth: CURRENT_NAME_MAX_PX + 80,
                '&:hover': { bgcolor: 'action.selected' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'common.white',
                  outlineOffset: '2px'
                }
              }}
            >
              <Typography
                component="span"
                variant="body2"
                fontWeight={600}
                color="text.primary"
                noWrap
                sx={{ maxWidth: CURRENT_NAME_MAX_PX }}
              >
                {current.name}
              </Typography>
              <ExpandMore sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
            </ButtonBase>
          </Tooltip>
        </Box>
      </Box>

      <Menu
        anchorEl={overflowAnchor}
        open={overflowOpen}
        onClose={() => setOverflowOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: '4px', minWidth: 200 } } }}
      >
        {hiddenAncestors.map((ancestor) => (
          <MenuItem
            key={ancestor.id}
            onClick={() => {
              onSelect(ancestor.id)
              setOverflowOpen(false)
            }}
          >
            <Typography variant="body2" noWrap>
              {ancestor.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      <OrgChildrenMenu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        currentOrg={current}
        childOrgs={childOrgs}
        childrenLoading={childrenLoading}
        ancestors={isMobile ? ancestors : []}
        onSelect={onSelect}
      />
    </Box>
  )
}

const crumbSx = {
  typography: 'body2',
  color: 'text.secondary',
  px: 1,
  py: 0.5,
  borderRadius: 0.5,
  '&:hover': {
    bgcolor: 'action.hover',
    color: 'text.primary'
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'common.white',
    outlineOffset: '2px'
  }
} as const

function CrumbButton({ children, onClick }: { children: string; onClick: () => void }) {
  return (
    <ButtonBase onClick={onClick} sx={crumbSx}>
      {children}
    </ButtonBase>
  )
}

function sameIdList(a: string[], b: string[]) {
  return a.length === b.length && a.every((id, index) => id === b[index])
}
